import { useCallback, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Contributions, ScoreMeter } from '@/components/charts/Contributions'
import {
  AssertionBadge,
  Avatar,
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  PageHeader,
  Spinner,
  StatusBadge,
} from '@/components/ui'
import { useQuery } from '@/hooks/useQuery'
import { ApiError, api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { dateTime, matchTypeLabel, sentence } from '@/lib/format'
import type { Ack, MatchDetail, MatchParty } from '@/types'

export function MatchDetailPage() {
  const { matchId } = useParams<{ matchId: string }>()
  const { canApprove } = useAuth()
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ tone: 'good' | 'critical'; text: string } | null>(null)
  const [editing, setEditing] = useState(false)
  const [drafts, setDrafts] = useState<{ a: string; b: string; joint: string } | null>(null)

  const query = useQuery(
    useCallback(() => api.get<MatchDetail>(`/matches/${matchId}`), [matchId]),
    [matchId],
  )

  async function run(label: string, action: () => Promise<unknown>, message: string) {
    setBusy(label)
    setNotice(null)
    try {
      await action()
      setNotice({ tone: 'good', text: message })
      query.reload()
    } catch (err) {
      setNotice({
        tone: 'critical',
        text: err instanceof ApiError ? err.message : 'That action failed.',
      })
    } finally {
      setBusy(null)
    }
  }

  if (query.loading && !query.data) return <Spinner label="Loading match" />
  if (query.error) return <ErrorNote message={query.error} onRetry={query.reload} />
  if (!query.data) return null

  const match = query.data
  const undecided = match.status === 'new' || match.status === 'reviewing'

  return (
    <>
      <PageHeader
        title={`${match.person_a.name} ⇄ ${match.person_b.name}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone="info">{matchTypeLabel(match.match_type)}</Badge>
            <StatusBadge status={match.status} />
            <span className="text-ink-muted">
              {sentence(match.direction)} · engine v{match.engine_version}
            </span>
          </span>
        }
        action={
          <Link to="/opportunities" className="btn-secondary">
            ← Back to queue
          </Link>
        }
      />

      {notice && (
        <p
          className="mb-4 rounded-md px-3 py-2 text-sm"
          role="status"
          style={{
            background: `color-mix(in srgb, var(--${notice.tone}) 12%, transparent)`,
            color: `var(--${notice.tone})`,
          }}
        >
          {notice.text}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <Card title="The opportunity">
            <div className="space-y-4 px-4 py-4">
              <div className="flex flex-col gap-3 sm:flex-row">
                <PartyCard party={match.person_a} label="Person A" />
                <PartyCard party={match.person_b} label="Person B" />
              </div>

              {match.opportunity_text && (
                <p className="text-sm text-ink">{match.opportunity_text}</p>
              )}

              {match.caps_applied.length > 0 && (
                <ul className="space-y-1">
                  {match.caps_applied.map((cap) => (
                    <li key={cap} className="text-xs" style={{ color: 'var(--serious)' }}>
                      ⚠ {cap}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          <Card
            title="Why they match"
            subtitle="Each reason is marked with whether the person stated it or we inferred it"
          >
            {match.reasons.length === 0 ? (
              <EmptyState title="No reasons were recorded" />
            ) : (
              <ul className="divide-y divide-line">
                {match.reasons.map((reason, index) => (
                  <li key={index} className="flex items-start gap-3 px-4 py-3">
                    <span className="mt-0.5">
                      <AssertionBadge assertion={reason.assertion} />
                    </span>
                    <span className="text-sm text-ink">{reason.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="Evidence"
            subtitle="Stated needs and offers behind this suggestion"
          >
            {match.evidence.length === 0 ? (
              <EmptyState title="No explicit evidence was attached" />
            ) : (
              <ul className="divide-y divide-line">
                {match.evidence.map((item, index) => (
                  <li key={index} className="flex items-start justify-between gap-3 px-4 py-3">
                    <span className="min-w-0">
                      <span className="text-xs text-ink-faint">
                        Person {String(item.side).toUpperCase()} ·{' '}
                        {item.kind === 'need' ? 'needs' : 'offers'}
                      </span>
                      <span className="block text-sm text-ink">{String(item.text)}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <Badge tone={item.urgency === 'urgent' ? 'warning' : 'neutral'}>
                        {sentence(String(item.urgency))}
                      </Badge>
                      <span className="text-xs tabular text-ink-faint">
                        {Math.round(Number(item.confidence) * 100)}%
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="How the score was built"
            subtitle={`${match.score}/100 = 45% engine (${match.deterministic_score}) + 55% AI (${match.llm_score ?? '—'}), then capped`}
          >
            <Contributions features={match.feature_explanations} />
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Decision">
            <div className="space-y-3 px-4 py-4">
              <ScoreMeter score={match.score} />
              <dl className="space-y-1.5 text-xs">
                <Row label="Confidence" value={`${Math.round(match.confidence * 100)}%`} />
                <Row label="Computed" value={dateTime(match.computed_at)} />
                {match.reviewed_at && (
                  <Row label="Reviewed" value={dateTime(match.reviewed_at)} />
                )}
              </dl>

              {match.review_note && (
                <p className="rounded-md px-3 py-2 text-xs text-ink-muted" style={{ background: 'var(--surface-2)' }}>
                  {match.review_note}
                </p>
              )}

              {!canApprove ? (
                <p className="text-xs text-ink-faint">
                  Your role can review matches but not approve them.
                </p>
              ) : undecided ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary flex-1"
                    disabled={busy !== null}
                    onClick={() =>
                      run(
                        'reject',
                        () => api.post(`/matches/${match.id}/review`, { action: 'reject' }),
                        'Match rejected.',
                      )
                    }
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="btn-primary flex-1"
                    disabled={busy !== null}
                    onClick={() =>
                      run(
                        'approve',
                        () => api.post(`/matches/${match.id}/review`, { action: 'approve' }),
                        'Match approved. You can now request consent.',
                      )
                    }
                  >
                    Approve
                  </button>
                </div>
              ) : match.status === 'approved' ? (
                <>
                  <button
                    type="button"
                    className="btn-primary w-full"
                    disabled={busy !== null}
                    onClick={() =>
                      run(
                        'intro',
                        () => api.post<Ack>(`/matches/${match.id}/request-intro`),
                        'Consent requests queued for both people.',
                      )
                    }
                  >
                    {busy === 'intro' ? 'Queueing…' : 'Ask both for consent'}
                  </button>
                  <p className="text-xs text-ink-faint">
                    Each person is asked separately and described in the abstract. Neither name
                    is revealed until both say yes.
                  </p>
                </>
              ) : (
                <Link to="/introductions" className="btn-secondary w-full">
                  Track the introduction →
                </Link>
              )}
            </div>
          </Card>

          <Card
            title="Suggested messages"
            subtitle="Edit before anything is sent"
            action={
              canApprove && (
                <button
                  type="button"
                  className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => {
                    setDrafts({
                      a: match.suggested_intro_a ?? '',
                      b: match.suggested_intro_b ?? '',
                      joint: match.suggested_intro_joint ?? '',
                    })
                    setEditing((value) => !value)
                  }}
                >
                  {editing ? 'Cancel' : 'Edit'}
                </button>
              )
            }
          >
            {!match.suggested_intro_a && !editing ? (
              <EmptyState
                title="Not drafted yet"
                hint="Copy is written when you request consent."
              />
            ) : editing && drafts ? (
              <div className="space-y-3 px-4 py-4">
                <Field label={`Message to ${match.person_a.name}`}>
                  <textarea
                    className="field h-20 text-xs"
                    value={drafts.a}
                    onChange={(event) => setDrafts({ ...drafts, a: event.target.value })}
                  />
                </Field>
                <Field label={`Message to ${match.person_b.name}`}>
                  <textarea
                    className="field h-20 text-xs"
                    value={drafts.b}
                    onChange={(event) => setDrafts({ ...drafts, b: event.target.value })}
                  />
                </Field>
                <Field label="Joint introduction">
                  <textarea
                    className="field h-20 text-xs"
                    value={drafts.joint}
                    onChange={(event) => setDrafts({ ...drafts, joint: event.target.value })}
                  />
                </Field>
                <button
                  type="button"
                  className="btn-primary w-full"
                  disabled={busy !== null}
                  onClick={() =>
                    run(
                      'save',
                      async () => {
                        await api.patch(`/matches/${match.id}`, {
                          suggested_intro_a: drafts.a,
                          suggested_intro_b: drafts.b,
                          suggested_intro_joint: drafts.joint,
                        })
                        setEditing(false)
                      },
                      'Copy saved.',
                    )
                  }
                >
                  Save copy
                </button>
              </div>
            ) : (
              <div className="space-y-3 px-4 py-4 text-xs">
                <Draft label={`To ${match.person_a.name}`} text={match.suggested_intro_a} />
                <Draft label={`To ${match.person_b.name}`} text={match.suggested_intro_b} />
                <Draft label="Joint" text={match.suggested_intro_joint} />
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}

function PartyCard({ party, label }: { party: MatchParty; label: string }) {
  return (
    <Link
      to={`/contacts/${party.contact_id}`}
      className="flex-1 rounded-md border border-line px-3 py-2.5 transition-colors hover:bg-raised"
    >
      <p className="text-xs text-ink-faint">{label}</p>
      <span className="mt-1 flex items-center gap-2">
        <Avatar name={party.name} size={30} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-ink">{party.name}</span>
          <span className="block truncate text-xs text-ink-muted">{party.headline || '—'}</span>
        </span>
      </span>
      {party.location && (
        <p className="mt-1.5 truncate text-xs text-ink-faint">{party.location}</p>
      )}
    </Link>
  )
}

function Draft({ label, text }: { label: string; text: string | null }) {
  if (!text) return null
  return (
    <div>
      <p className="mb-1 font-medium text-ink-faint">{label}</p>
      <p className="rounded-md px-3 py-2 text-ink" style={{ background: 'var(--surface-2)' }}>
        {text}
      </p>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right text-ink">{value}</dd>
    </div>
  )
}
